import { cn } from '@/lib/utils';
import { 
  NewTicketsStatsIcon, 
  MyTicketsStatsIcon, 
  WaitingTicketsStatsIcon, 
  OpenTicketsStatsIcon 
} from '@/config/icons';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RetryErrorCard } from '@/components/ui/retry-error-card';
import { useDashboardStats } from '@/hooks/useDashboardQueries';

const colorClasses = {
  pink: {
    border: 'border-l-luminjo-pink',
    bg: 'bg-luminjo-pink-light',
    text: 'text-luminjo-pink',
    iconBg: 'bg-luminjo-pink/10',
  },
  blue: {
    border: 'border-l-primary',
    bg: 'bg-primary/5',
    text: 'text-primary',
    iconBg: 'bg-primary/10',
  },
  orange: {
    border: 'border-l-accent',
    bg: 'bg-accent/5',
    text: 'text-accent',
    iconBg: 'bg-accent/10',
  },
  green: {
    border: 'border-l-luminjo-success',
    bg: 'bg-luminjo-success/5',
    text: 'text-luminjo-success',
    iconBg: 'bg-luminjo-success/10',
  },
};

function TrendBadge({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  const percentage = previous > 0 ? Math.round((diff / previous) * 100) : (current > 0 ? 100 : 0);
  
  if (diff === 0) {
    return (
      <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" />
        <span>0%</span>
      </span>
    );
  }
  
  if (diff > 0) {
    return (
      <span className="flex items-center gap-0.5 text-xs text-luminjo-success">
        <TrendingUp className="h-3 w-3" />
        <span>+{percentage}%</span>
      </span>
    );
  }
  
  return (
    <span className="flex items-center gap-0.5 text-xs text-destructive">
      <TrendingDown className="h-3 w-3" />
      <span>{percentage}%</span>
    </span>
  );
}

export function StatsCards() {
  const { activeWorkspace } = useWorkspaceContext();
  const { user } = useAuth();

  const { data: stats, isLoading, error, refetch, isRefetching } = useDashboardStats(
    activeWorkspace?.id,
    user?.id
  );

  const statCards = [
    { 
      title: 'Nouveaux tickets', 
      data: stats?.newTickets,
      icon: NewTicketsStatsIcon, 
      color: 'pink' as const,
      link: '/tickets?status=new'
    },
    { 
      title: 'Mes tickets', 
      data: stats?.myTickets,
      icon: MyTicketsStatsIcon, 
      color: 'blue' as const,
      link: '/tickets?assigned=me'
    },
    { 
      title: 'Tickets en attente', 
      data: stats?.waitingTickets,
      icon: WaitingTicketsStatsIcon, 
      color: 'orange' as const,
      link: '/tickets?status=pending'
    },
    { 
      title: 'Tickets ouverts', 
      data: stats?.openTickets,
      icon: OpenTicketsStatsIcon, 
      color: 'green' as const,
      link: '/tickets?status=open'
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-card rounded-lg border border-border shadow-sm p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-12" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-9 w-9 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm">
        <RetryErrorCard
          message="Impossible de charger les statistiques"
          onRetry={() => refetch()}
          isRetrying={isRefetching}
          retryCount={0}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat) => {
        const colors = colorClasses[stat.color];
        return (
          <Link
            key={stat.title}
            to={stat.link}
            className={cn(
              "block bg-card rounded-lg border border-border shadow-sm p-4",
              "border-l-4 transition-all hover:shadow-md hover:scale-[1.02]",
              colors.border
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className={cn("text-sm font-medium", colors.text)}>
                  {stat.title}
                </p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {stat.data?.current ?? 0}
                </p>
                <div className="mt-1">
                  <TrendBadge 
                    current={stat.data?.current ?? 0} 
                    previous={stat.data?.previous ?? 0} 
                  />
                </div>
              </div>
              <div className={cn("p-2 rounded-lg", colors.iconBg)}>
                <stat.icon className={cn("h-5 w-5", colors.text)} />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
